// src/components/SectionThree.js
import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { Bar } from 'react-chartjs-2'
import { Chart, registerables } from 'chart.js'
import dayjs from 'dayjs'
import { Card, Row, Col } from 'react-bootstrap'
import { fetchDashboardMonthlyStats } from '../apiClient'

Chart.register(...registerables)

export default function SectionThree({ data }) {
  const [monthlyChartData, setMonthlyChartData] = useState({
    labels: [],
    datasets: [],
  })
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const num = (v) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }

  const loadMonthlyStats = useCallback(async () => {
    try {
      setLoading(true)
      setErrorMsg('')

      // Who is logged in?
      const { data: u } = await supabase.auth.getUser()
      const uid = u?.user?.id || null

      let role = 'user'
      if (uid) {
        const { data: roleRow } = await supabase
          .from('approved_users')
          .select('role')
          .eq('user_id', uid)
          .maybeSingle()

        if (roleRow?.role) {
          role = roleRow.role
        }
      }

      // Call backend monthly-stats (role-based filtering is applied there)
      const resp = await fetchDashboardMonthlyStats(uid, role)

      if (resp.status !== 'ok') {
        setErrorMsg('Failed to load monthly stats.')
        setMonthlyChartData({ labels: [], datasets: [] })
        return
      }

      const labels = (resp.months || []).map((m) => m.label)
      const collectedData = (resp.months || []).map((m) => num(m.collected))
      const expectedData = (resp.months || []).map((m) => num(m.expected))

      setMonthlyChartData({
        labels,
        datasets: [
          {
            label: 'Collected',
            backgroundColor: '#4caf50',
            data: collectedData,
          },
          {
            label: 'Expected',
            backgroundColor: '#f44336',
            data: expectedData,
          },
        ],
      })
    } catch (err) {
      console.error('[SectionThree] monthly stats error:', err)
      setErrorMsg('Failed to load monthly stats.')
      setMonthlyChartData({ labels: [], datasets: [] })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadMonthlyStats().catch(() => {})
  }, [loadMonthlyStats])

  const hasData = monthlyChartData.labels && monthlyChartData.labels.length > 0

  return (
    <div className="container-fluid">
      <Row>
        {/* Bar Chart */}
        <Col md={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title>Monthly Collection (Last 3 Months)</Card.Title>

              {errorMsg && (
                <div className="alert alert-light py-2 text-muted border mb-2">
                  {errorMsg}
                </div>
              )}

              {loading && !hasData ? (
                <div className="text-center text-muted py-4">Loading…</div>
              ) : hasData ? (
                <Bar
                  data={monthlyChartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const label = ctx.dataset.label || ''
                            const val = ctx.raw ?? 0
                            return `${label}: ${Number(val).toLocaleString()} TZS`
                          },
                        },
                      },
                    },
                    scales: {
                      y: {
                        ticks: {
                          callback: (v) => Number(v).toLocaleString(),
                        },
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              ) : (
                <div className="text-muted py-4">
                  No monthly stats available for this period.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Scrollable Vehicle Collection List (still from dashboard summary) */}
        <Col md={6} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title>Vehicle-wise Collection</Card.Title>
              <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                {(data?.topVehicles || []).map((v, index) => (
                  <div
                    key={index}
                    className="d-flex justify-content-between align-items-center border-bottom py-2"
                  >
                    <div>
                      <strong>{v.plate_number}</strong>
                      <div
                        style={{
                          fontSize: '0.85rem',
                          color: '#666',
                        }}
                      >
                        {v.driver_name}
                      </div>
                    </div>
                    <div>
                      {Number(v.collected || 0).toLocaleString()} TZS
                    </div>
                  </div>
                ))}
                {(!data?.topVehicles || data.topVehicles.length === 0) && (
                  <div className="text-muted">No vehicle collections to show.</div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}